import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./CoachAbout.module.scss";

interface Props {
  /** The coach's bio, from their own profile. Nothing renders without one. */
  text?: string;
}

/**
 * The coach's bio above the tabs (frame 43592:26426): three lines, then
 * "Show More".
 *
 * The toggle only appears when there is something behind it, which has to be
 * measured — whether three lines is a clamp depends on the words and on how wide
 * the window is. The public landing page measures its own the same way; this is
 * the app-native page's, so nothing here depends on the `pub-*` stylesheet those
 * rules live in.
 */
const CoachAbout: React.FC<Props> = ({ text }) => {
  const { t } = useTranslation();
  const textRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);

  useEffect(() => {
    const measure = () => {
      const element = textRef.current;
      if (!element || expanded) {
        return;
      }
      setClamped(element.scrollHeight > element.clientHeight + 1);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text, expanded]);

  if (!text || text.trim() === "") {
    return null;
  }

  return (
    <section className={styles.about}>
      <h2 className={styles.title}>{t("About")}</h2>
      <p
        ref={textRef}
        className={expanded ? styles.text : styles.text_clamped}
        data-testid="coach-about"
      >
        {text}
      </p>
      {(clamped || expanded) && (
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setExpanded((open) => !open)}
          data-testid="coach-about-toggle"
        >
          {expanded ? t("Show Less") : t("Show More")}
        </button>
      )}
    </section>
  );
};

export default CoachAbout;
